import { Info } from '@mui/icons-material';
import { getCompanyConfigOld } from 'lib/api/company';
import { getBalancesReport, getEFAReport } from 'lib/api/reports';
import { BalanceReportItem } from 'lib/api/reports/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

const Balances: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BalanceReportItem[]>([]);
  const [filteredData, setFilteredData] = useState<BalanceReportItem[]>([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [configurations, setConfigurations] = useState<{
    factoringBelongsToEfa: boolean;
  }>({
    factoringBelongsToEfa: false,
  });

  const filters: Filter[] = [
    {
      label: 'Nombre',
      type: 'localAutocomplete',
      value: businessName,
      setter: setBusinessName,
      options: data.map((x: BalanceReportItem) => ({
        label: x.businessName,
        value: x.businessName,
      })),
    },
    {
      label: 'Rut',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
  ];

  const handleShowBusinessDetails = (id: string) => {
    const row = data.find(
      (x: BalanceReportItem) => x.businessIdentifier === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const fetchData = async () => {
    const response = await getBalancesReport(dispatch);
    if (!response) return;
    setData([...response]);
    setFilteredData([...response]);
    return response;
  };

  const triggerSearch = async () => {
    const filtered = data.filter((x: BalanceReportItem) => {
      if (businessName) return x.businessName === businessName;
      if (businessIdentifier)
        return x.businessIdentifier === businessIdentifier;
      return true;
    });
    setFilteredData(filtered);
  };

  const fetchConfigurations = async () => {
    const response = await getCompanyConfigOld(
      dispatch,
      'factoring_belongs_to_efa',
    );
    if (!response) return;
    let factoringBelongsToEfa = false;
    if (response === '1') {
      factoringBelongsToEfa = true;
    }
    setConfigurations({
      factoringBelongsToEfa: factoringBelongsToEfa,
    });
    return response;
  };

  const setUp = async () => {
    setLoading(true);
    try {
      await fetchConfigurations();
      await fetchData();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener los datos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleDownloadEFA = async () => {
    setLoading(true);
    try {
      const response = await getEFAReport(dispatch);
      if (!response) return;
      const { fileContent, fileName } = response;
      const blob = new Blob([fileContent], {
        type: 'text/plain;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el informe EFA:', error);
      showAlert(dispatch, 'error', 'Error al descargar el informe EFA');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá verás todos los saldos por empresa."
        buttons={[
          ...(configurations.factoringBelongsToEfa
            ? [
                {
                  name: 'Informe EFA',
                  action: () => handleDownloadEFA(),
                  color: 'primary' as const,
                },
              ]
            : []),
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary' as const,
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
              />
            ),
          },
        ]}
      />

      <EnhancedTable
        dataHeaders={[
          {
            key: ' ',
            label: '',
            handleAction: handleShowBusinessDetails,
            icon: <Info fontSize="small" />,
          },
          {
            key: 'businessName',
            label: 'Nombre',
          },
          { key: 'businessIdentifier', label: 'Rut' },
          { key: 'inFavor', label: 'Monto a favor', type: 'money' },
          { key: 'against', label: 'Monto en contra sin mora', type: 'money' },
          { key: 'arrears', label: 'Monto de mora proyectada', type: 'money' },
          {
            key: 'againstWithArrears',
            label: 'Monto en contra con mora proyectada',
            type: 'money',
          },
          {
            key: 'factoringAsClient',
            label: 'Factoring como cliente',
            type: 'money',
          },
          {
            key: 'factoringAsStakeholder',
            label: 'Factoring como deudor',
            type: 'money',
          },
          {
            key: 'confirming',
            label: 'Confirming',
            type: 'money',
          },
          { key: 'credit', label: 'Créditos', type: 'money' },
          {
            key: 'inFavorPendingAccounts',
            label: 'Cuentas por pagar',
            type: 'money',
          },
          {
            key: 'againstPendingAccounts',
            label: 'Cuentas por cobrar',
            type: 'money',
          },
          { key: 'onDate', label: 'Vigente', type: 'money' },
          {
            key: 'dued0to30Days',
            label: 'Moroso 0-30 ',
            type: 'money',
          },
          {
            key: 'dued31to60Days',
            label: 'Moroso 31-60 días',
            type: 'money',
          },
          {
            key: 'dued61to90Days',
            label: 'Moroso 61-90 días',
            type: 'money',
          },
          {
            key: 'duedMore90Days',
            label: 'Moroso más de 90 días',
            type: 'money',
          },
        ]}
        data={filteredData}
        rowsPerPageDefault={25}
        lastElement
        exportToFile="xlsx"
        finalSumRow={true}
        exportHeaders={[
          {
            key: 'businessName',
            label: 'Nombre',
          },
          { key: 'businessIdentifier', label: 'Rut' },
          { key: 'inFavor', label: 'Monto a favor', type: 'money' },
          { key: 'against', label: 'Monto en contra sin mora', type: 'money' },
          { key: 'arrears', label: 'Monto de mora proyectada', type: 'money' },
          {
            key: 'againstWithArrears',
            label: 'Monto en contra con mora proyectada',
            type: 'money',
          },
          {
            key: 'factoringAsClient',
            label: 'Factoring como cliente',
            type: 'money',
          },
          {
            key: 'factoringAsStakeholder',
            label: 'Factoring como deudor',
            type: 'money',
          },
          {
            key: 'confirming',
            label: 'Confirming',
            type: 'money',
          },
          { key: 'credit', label: 'Créditos', type: 'money' },
          {
            key: 'inFavorPendingAccounts',
            label: 'Cuentas por pagar',
            type: 'money',
          },
          {
            key: 'againstPendingAccounts',
            label: 'Cuentas por cobrar',
            type: 'money',
          },
          { key: 'onDate', label: 'Vigente', type: 'money' },
          {
            key: 'dued0to30Days',
            label: 'Moroso 0-30 ',
            type: 'money',
          },
          {
            key: 'dued31to60Days',
            label: 'Moroso 31-60 días',
            type: 'money',
          },
          {
            key: 'dued61to90Days',
            label: 'Moroso 61-90 días',
            type: 'money',
          },
          {
            key: 'duedMore90Days',
            label: 'Moroso más de 90 días',
            type: 'money',
          },
        ]}
      />
    </div>
  );
};

export default Balances;
